import { useCallback, useEffect, useState } from 'react';
import type { Indicador } from '../../api/ops/indicadores';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import { valorDeApartado } from '../apartados';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { Catalogo } from './Catalogo';
import { Exportaciones } from './Exportaciones';
import '../../componentes/apartado.css';
import './indicadores.css';

/**
 * «Indicadores» (Seguimiento, entrada `indicadores`; 7.2 y 7.3): la barra Indicadores · Exportaciones. «Exportaciones» es
 * la entrada hija `indicadores_exportaciones` (`nested_as: section`, permiso `analitica.indicadores.exportar`) y solo
 * existe si llega (o si la persona tiene el permiso): el Socio, el Profesional, el RRPP y Recepción no la ven. Los dos
 * apartados comparten el catálogo (`listIndicators`, todas las páginas) y las sedes. 7.4 (Backoffice) no está en el panel
 * del negocio (G427).
 */
export interface DatosIndicadores {
    catalogo: Indicador[];
    sedes: { id: string; nombre: string }[];
}

export interface PropsApartado extends PropsPantalla {
    datos: DatosIndicadores;
    puedeExportar: boolean;
    /** Va a otro apartado con lo que haga falta en la dirección (`indicador=…`, `nueva=…`). */
    irA: (apartado: 'indicadores' | 'exportaciones', extra?: Record<string, string>) => void;
}

export const CLAVE_EXPORTACIONES = 'indicadores_exportaciones';

async function catalogoEntero(props: PropsPantalla): Promise<Indicador[]> {
    const todos: Indicador[] = [];
    let cursor: string | null = null;
    do {
        const pagina = await props.ops.listIndicators(props.negocio, { limit: 100, cursor });
        todos.push(...pagina.data);
        cursor = pagina.next_cursor;
    } while (cursor !== null);
    return todos;
}

export function Indicadores(props: PropsPantalla) {
    const { ops, negocio, busqueda, ruta, navegar, movil, membresia, nombreNegocio, anidadas } = props;
    const { puede } = usePermisos();
    const hija = (anidadas ?? []).find((e) => e.clave === CLAVE_EXPORTACIONES && e.anidadaComo === 'section' && !e.noContratada);
    const puedeExportar = hija !== undefined || puede('analitica.indicadores.exportar');
    const valorExportaciones = hija === undefined ? 'exportaciones' : valorDeApartado(hija.clave, hija.ruta, 'indicadores_');
    const apartados = [
        { valor: 'indicadores', etiqueta: 'Indicadores', icono: 'chart-line' as const, cifra: null },
        ...(puedeExportar ? [{ valor: valorExportaciones, etiqueta: hija?.etiqueta ?? 'Exportaciones', icono: 'download' as const, cifra: hija?.contador ?? null }] : []),
    ];
    const activo = apartados.find((a) => a.valor === busqueda.get('apartado')) ?? apartados[0];
    const enExportaciones = puedeExportar && activo?.valor === valorExportaciones;
    const [datos, setDatos] = useState<DatosIndicadores | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const reintentar = useCallback(() => {
        setError(null);
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let vivo = true;
        Promise.all([catalogoEntero({ ...props }), ops.listLocations(negocio).catch(() => null)])
            .then(([catalogo, sedes]) => {
                if (vivo) {
                    setDatos({ catalogo, sedes: (sedes?.data ?? []).filter((s) => s.status === 'active').map((s) => ({ id: s.id, nombre: s.name })) });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cambian el negocio y el reintento
    }, [ops, negocio, intento]);

    const seccion = enExportaciones ? 'tus exportaciones' : 'los indicadores';
    if (error !== null) {
        const e = estadoDeError(error);
        if (e.tipo === 'sin-permiso') {
            return <EstadoSinPermiso seccion="los indicadores" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        return <EstadoError seccion={seccion} codigoSoporte={e.codigoSoporte} onReintentar={reintentar} movil={movil} />;
    }
    if (datos === null || activo === undefined) {
        return <EstadoCargando seccion={seccion} />;
    }
    const base = ruta.split('?')[0] ?? ruta;
    const irA = (apartado: 'indicadores' | 'exportaciones', extra: Record<string, string> = {}) => {
        const valor = apartado === 'exportaciones' ? valorExportaciones : null;
        const resto = new URLSearchParams(extra).toString();
        const destino = rutaConApartado(base, valor);
        navegar(resto === '' ? destino : `${destino}${destino.includes('?') ? '&' : '?'}${resto}`);
    };
    const Apartado = enExportaciones ? Exportaciones : Catalogo;

    return (
        <div className="rv-en-pantalla">
            {apartados.length > 1 && (
                <div className="rv-en-barra">
                    <Segmentos
                        etiqueta="Apartados de indicadores"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.etiqueta, icono: a.icono, cifra: a.cifra }))}
                        valor={apartados.indexOf(activo)}
                        onCambio={(i) => {
                            irA(i === 0 ? 'indicadores' : 'exportaciones');
                        }}
                    />
                </div>
            )}
            <Apartado key={activo.valor} {...props} datos={datos} puedeExportar={puedeExportar} irA={irA} />
        </div>
    );
}
